import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Foldfire's shots (the Origami set's gun, ADR-0160 amended): a paper plane (turned along its flight, its wings tilting
 * as it flies), a puff of white with scraps of paper flung out at the muzzle, a sheet that unfolds where it lands and
 * tears into scraps; and the flourish's paper crane flying (`origami_crane`) and a scrap tumbling (`origami_scrap`).
 */

type Pt = readonly [number, number];

/** Whether (x, y) is inside the triangle a b c. */
function inTri(x: number, y: number, a: Pt, b: Pt, c: Pt): boolean {
  const s = (p: Pt, q: Pt) => (q[0] - p[0]) * (y - p[1]) - (q[1] - p[1]) * (x - p[0]);
  const [d1, d2, d3] = [s(a, b), s(b, c), s(c, a)];
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

/**
 * The plane, nose right at (28, 8): its top wing (white, lit) and its far wing under it (grey), its keel (a slate fold)
 * between; `tilt` lifts the far wing's tip (-1 … 1) as it rocks.
 */
const plane = (tilt: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const nose: Pt = [28, 8.5];
    const top = inTri(x, y, nose, [13, 3 - tilt], [15, 8.5]);
    const far = inTri(x, y, nose, [14, 13 + tilt], [15, 8.5]);
    const keel = inTri(x, y, nose, [16, 8.5], [17, 11]);
    if (keel) return '3';
    if (top) return x < 15.5 || y < 4.5 + (x - 13) * 0.3 ? '5' : 'w';
    if (far) return y > 12 - (x - 14) * 0.3 ? '4' : '5';
    return null;
  });

/** A scrap of paper at (cx, cy) turned by `a`: a little quad, white with a shaded half, or red with `red`. */
const scrap = (x: number, y: number, cx: number, cy: number, s: number, a: number, red: boolean): Tone => {
  const [dx, dy] = [x - cx, y - cy];
  const [u, v] = [dx * Math.cos(a) + dy * Math.sin(a), -dx * Math.sin(a) + dy * Math.cos(a)];
  if (Math.abs(u) + Math.abs(v) * 0.6 > s) return null;
  return red ? (u < 0 ? 'r' : 'R') : u + v < 0 ? 'w' : '5';
};

/** A white puff at the muzzle, scraps flung out of it, further and fewer each frame. */
const puff = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 6, y - 20);
    if (f < 2 && d < [6, 4][f]!) return d < [3.5, 2][f]! ? 'w' : '5';
    const reach = [10, 18, 25][f] as number;
    for (let i = 0; i < 6; i++) {
      if (f === 2 && i % 2) continue;
      const a = (i - 2.5) * 0.28;
      const r = reach * (0.75 + ((i * 7) % 4) * 0.1);
      const k = scrap(x, y, 6 + Math.cos(a) * r, 20 + Math.sin(a) * r, [2.6, 2.2, 1.6][f]!, i + f, i === 1 || i === 4);
      if (k) return k;
    }
    return null;
  });

/**
 * Where it lands: a sheet of paper unfolding flat (a diamond creased in an X), then torn: four quarters, then six
 * scraps flung further, a few last bits.
 */
const tear = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    if (f === 0) {
      if (Math.abs(dx) + Math.abs(dy) > 10) return null;
      if (Math.abs(Math.abs(dx) + Math.abs(dy) - 10) < 1) return dx + dy < 0 ? '5' : '4';
      if (Math.abs(dx) < 0.6 || Math.abs(dy) < 0.6) return '4';
      return dx + dy < 0 ? 'w' : '5';
    }
    const n = [0, 4, 6, 4][f] as number;
    const R = [0, 7, 13, 17][f] as number;
    const s = [0, 4.5, 3, 2][f] as number;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + 0.4 + f * 0.3;
      const k = scrap(x, y, 22 + Math.cos(a) * R, 22 + Math.sin(a) * R * 0.85, s, a + f, f > 1 && i === 2);
      if (k) return k;
    }
    return null;
  });

/**
 * A paper crane in flight, facing right (16 × 12, its middle at (8, 6)): a diamond body, a neck and pointed head
 * forward and up, a tail back; its wings beating over 4 frames (up, level, down, level). Rimmed in slate.
 */
const crane = (f: number) => (c: PixelCanvas) => {
  const tip = (
    [
      [6, 0],
      [4, 4],
      [6, 11],
      [4, 6],
    ] as const
  )[f] as Pt;
  const farTip = (
    [
      [9, 0.5],
      [10, 4],
      [9, 10],
      [10, 5],
    ] as const
  )[f] as Pt;
  paint(c, (x, y) => {
    if (inTri(x, y, [5, 6.5], [10, 6.5], tip)) return 'w';
    if (Math.abs(x - 7.5) / 4.5 + Math.abs(y - 7) / 1.6 <= 1) return y < 7 ? 'w' : '5';
    // Neck and head forward.
    if (x >= 11 && x < 15 && Math.abs(y - (7 - (x - 11) * 1.1)) < 0.8) return x > 14 ? '4' : '5';
    // Tail back.
    if (x >= 1 && x < 4 && Math.abs(y - (7 - (4 - x) * 0.9)) < 0.8) return '5';
    if (inTri(x, y, [8, 6.5], [11, 6.5], farTip)) return '4';
    return null;
  });
  rim(c);
};

/** A 1-px slate rim round what's drawn (the fx's own outline weight is 1 px at their size). */
function rim(c: PixelCanvas): void {
  const filled = (x: number, y: number) => x >= 0 && y >= 0 && x < c.w && y < c.h && c.get(x, y) !== null;
  const edge: Pt[] = [];
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++)
      if (!filled(x, y) && (filled(x - 1, y) || filled(x + 1, y) || filled(x, y - 1) || filled(x, y + 1)))
        edge.push([x, y]);
  for (const [x, y] of edge) c.set(x, y, '3' as PaletteKey);
}

/** A scrap tumbling: face on, turning, edge on, turning back. */
const tumble = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const w = [2.5, 1.6, 0.6, 1.6][f] as number;
    if (Math.abs(x - 4) > w || Math.abs(y - 4) > 2.5 - Math.abs(x - 4) * 0.3) return null;
    return f === 2 ? '5' : x < 4 ? 'w' : '5';
  });

export default [
  sprite({
    name: 'fold_shot',
    ...shot,
    notes: `Foldfire's bullet (ADR-0160 amended): a paper plane, its nose on the pellet's head, a white top wing lit, its
far wing grey under it, a slate keel between; it rocks as it flies (the far wing's tip lifting and dropping over 4
frames). Turned along its flight. Rubric (4×): S5 R4 P5 L4 C4 A4 F4. Weakest: 16 px long, at 1:1 a white dart (its
cream tracer and glow carry it).`,
    anims: { idle: { fps: 12, frames: [plane(0), plane(1), plane(0), plane(-1)] } },
  }),
  sprite({
    name: 'fold_muzzle',
    ...muzzle,
    notes: `Foldfire's muzzle flash (ADR-0160 amended): a white puff at the muzzle, six scraps of paper (two red, the seal's)
flung out of it in a fan, further, smaller and fewer frame by frame. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(puff) } },
  }),
  sprite({
    name: 'fold_burst',
    ...burst,
    notes: `Foldfire's hit (ADR-0160 amended): the plane unfolds flat (a white sheet creased in an X), tears in four, then six
scraps flung out turning (one red), the last four. 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(tear) } },
  }),
  sprite({
    name: 'origami_crane',
    group: 'fx',
    size: [16, 12],
    origin: [8, 6],
    density: 4,
    notes: `The Origami's knock-out flock (ADR-0160 amended): a paper crane in flight facing right (a diamond body, a neck to
a pointed head, a tail), white wings beating over 4 frames, rimmed in slate; the flourish tints some. Rubric (4×): S4 R4
P5 L4 C4 A4 F4.`,
    anims: { fly: { fps: 14, frames: frames4(crane) } },
  }),
  sprite({
    name: 'origami_scrap',
    group: 'fx',
    size: [8, 8],
    origin: [4, 4],
    density: 4,
    notes: `The Origami's knock-out scrap (ADR-0160 amended): a scrap of paper tumbling (face on, turning, edge on, back).
Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(tumble) } },
  }),
];
